import api, { apiPrefix } from 'helpers/api'
import { APIError } from 'helpers/apiError'
import { getAuthToken } from 'helpers/auth'
import {
  deleteTableRowAction,
  updateRowPosition,
  updateTableViewCellsAction
} from 'components/spreadsheet/contexts/data/actions'
import { SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { getRowValue, resolveDependencyCachedOptions } from 'components/spreadsheet/helpers/functions'
import { getRowLockState } from 'components/spreadsheet/helpers/locking'
import { buildOptionsSet } from 'components/spreadsheet/helpers/validation'
import { ISpreadsheetData, SpreadsheetReducerActions } from 'components/spreadsheet/types'
import { ResolvedTheme } from 'helpers/theme'
import { ICellValue, IFile } from 'types'
import { BridgeRequest, PageData, PageFile } from './runtime'
import { rowSettings } from './access'
import { PageTheme } from './theme'
import { allOptions, pageRowReader, resolveBindings, valueStyle, visibleColumns, visibleViewColumns } from './source'

/** Reads answered at once from the columns the grid holds; they change nothing, so they skip the request queue. */
type InstantRequest = Extract<BridgeRequest, { method: 'options' | 'colour' | 'bind' }>
type WriteRequest = Exclude<BridgeRequest, { method: 'snapshot' | 'file' | 'download' } | InstantRequest>
type OptionsRequest = Extract<BridgeRequest, { method: 'options' }>
type FileRequest = Extract<BridgeRequest, { method: 'file' | 'download' }>

/** A problem with the page's own request, safe to report back to the page verbatim. */
class BridgeError extends Error {}

/** The table view's data context: the page writes through the same functions the grid uses. */
export interface GridContext {
  projectId: string
  /** The latest spreadsheet data; read afresh after each step because every write updates it. */
  current: () => ISpreadsheetData
  dispatch: React.Dispatch<SpreadsheetReducerActions>
  createRow: (values?: Record<string, ICellValue>, nextRowPublicId?: string, fromForm?: boolean) => Promise<void>
  /** The page's theme, so the rows a write returns are coloured for the reader's light or dark theme. */
  theme?: PageTheme
}

// Lets React commit a dispatch so current() reflects it (the context updates on the next render).
const settle = () => new Promise((resolve) => setTimeout(resolve, 0))

function checkColumns(values: Record<string, unknown>, columns: PageData['columns']): void {
  const known = new Set(columns.map((column) => column.id))
  const unknown = Object.keys(values).find((id) => !known.has(id))
  if (unknown) throw new BridgeError(`Column ${unknown} is not part of this view.`)
}

// As the grid does, formula, script, joined and locked columns (and everything, for viewers) can't be written.
function checkWritable(values: Record<string, unknown>, columns: PageData['columns']): void {
  checkColumns(values, columns)
  const readOnly = columns.find((column) => column.readOnly && column.id in values)
  if (readOnly) {
    throw new BridgeError(
      `Column "${readOnly.name}" is read-only here (calculated, locked, or you can't edit this table). Leave it out of the values you save.`
    )
  }
}

function viewColumn(columnId: string, data: ISpreadsheetData) {
  const column = visibleViewColumns(data).find((candidate) => candidate.publicId === columnId)
  if (!column) throw new BridgeError(`Column ${columnId} is not part of this view.`)
  return column
}

/** A select column's options, narrowed by the values of the columns they depend on, as the grid's select does. */
export function readOptions(params: OptionsRequest['params'], data: ISpreadsheetData): { options: string[] } {
  const column = viewColumn(params.columnId, data)
  if (!SELECT_COLUMN_TYPES.includes(column.kind)) {
    throw new BridgeError(`Column "${column.name}" is not a select column, so it has no options.`)
  }
  const tableOptions = column.kindOptions?.tableOptions
  if (!tableOptions?.dependencies?.length) return { options: allOptions(column) }
  const cached = resolveDependencyCachedOptions(
    (tableOptions.cachedOptions ?? {}) as { [key: string]: string[] },
    tableOptions.dependencies,
    (columnId) => params.values[columnId] as ICellValue | undefined
  )
  return { options: [...buildOptionsSet(cached, column.kindOptions?.manualOptions ?? [])] }
}

/** Answer a read that skips the request queue. */
export function runInstant(request: InstantRequest, data: ISpreadsheetData, mode: ResolvedTheme = 'light'): unknown {
  if (request.method === 'options') return readOptions(request.params, data)
  // Bound elements' values, worked out over every row the grid holds and formatted as the grid formats them.
  if (request.method === 'bind') return resolveBindings(data, request.params.bindings)
  // A value's look from the view's colour rules, e.g. for a select option's pill; null when no rule styles it.
  return { style: valueStyle(data, viewColumn(request.params.columnId, data), request.params.value, mode) }
}

// Like the grid, a page can only act on rows the reader's table view holds.
function checkRows(ids: string[], data: ISpreadsheetData): void {
  const known = new Set(data.rows.map((row) => row.publicId))
  const unknown = ids.find((id) => !known.has(id))
  if (unknown) throw new BridgeError(`Row ${unknown} is not part of this view.`)
}

// As the grid's delete does (getRowIdsToDelete), drop rows a row-lock rule protects.
function unlockedRows(ids: string[], data: ISpreadsheetData): string[] {
  const rules = data.userConfiguration.rowLockSettings
  if (rules.length === 0) return ids
  const columns = new Map(data.viewDetails.columns.map((column) => [column.publicId, column]))
  const rowsById = new Map(data.rows.map((row) => [row.publicId, row]))
  return ids.filter((id) => {
    const row = rowsById.get(id)
    return (
      row &&
      !getRowLockState(rules, row, columns, data.columnIdToIndex, data.columnValuesCount, data.columnStore).rowLocked
    )
  })
}

function pageRows(ids: string[], grid: GridContext) {
  const data = grid.current()
  const columns = visibleColumns(data, grid.theme)
  const byId = new Map(data.rows.map((row) => [row.publicId, row]))
  const toPageRow = pageRowReader(columns, data, grid.theme?.mode)
  return ids.flatMap((id) => {
    const row = byId.get(id)
    return row ? [toPageRow(row)] : []
  })
}

function contextFor(grid: GridContext) {
  const data = grid.current()
  return {
    projectId: grid.projectId,
    ...(data.processId
      ? {
          processPublicId: data.processId,
          processSectionPublicId: data.processSectionId!
        }
      : {})
  }
}

/** Grid actions report success and failure through callbacks; the page awaits a promise instead. */
function run(start: (resolve: () => void, reject: (error: unknown) => void) => void): Promise<void> {
  return new Promise((resolve, reject) => start(resolve, reject))
}

export async function runWrite(request: WriteRequest, grid: GridContext): Promise<unknown> {
  const columns = visibleColumns(grid.current(), grid.theme)
  switch (request.method) {
    case 'updateRows': {
      checkRows(
        request.params.rows.map((row) => row.id),
        grid.current()
      )
      request.params.rows.forEach((row) => checkWritable(row.values, columns))
      const cells = request.params.rows.flatMap((row) =>
        Object.entries(row.values).map(([columnId, value]) => ({ rowId: row.id, columnId, value: value as ICellValue }))
      )
      const data = grid.current()
      // As a grid edit: optimistic update, then the cells endpoint (which refreshes computed columns,
      // records undo history, and reverts the optimistic values if it fails).
      grid.dispatch({
        type: 'BULK_EDIT_CELL',
        cells: cells.map((cell) => ({ ...cell, value: { [cell.columnId]: cell.value } }))
      })
      await run((resolve, reject) =>
        updateTableViewCellsAction(
          data.viewDetails.publicId,
          { cells, context: contextFor(grid) },
          data,
          grid.dispatch,
          resolve,
          reject
        )
      )
      await settle()
      return {
        rows: pageRows(
          request.params.rows.map((row) => row.id),
          grid
        )
      }
    }
    case 'addRows': {
      if (!rowSettings(grid.current()).add) {
        throw new BridgeError(
          'Adding rows is turned off for this view (Allow Add New Row in its settings, or the table is synced).'
        )
      }
      request.params.rows.forEach((row) => checkWritable(row.values, columns))
      const created: string[] = []
      for (const row of request.params.rows) {
        const before = new Set(grid.current().rows.map((existing) => existing.publicId))
        // fromForm makes the grid's create-row rethrow failures instead of showing its own toast.
        await grid.createRow(row.values as Record<string, ICellValue>, undefined, true)
        await settle()
        const added = grid.current().rows.find((existing) => !before.has(existing.publicId))
        if (added) created.push(added.publicId)
      }
      return { rows: pageRows(created, grid) }
    }
    case 'deleteRows': {
      if (!rowSettings(grid.current()).delete) {
        throw new BridgeError('Deleting rows is not allowed in this view (Allow Row Deletion in its settings).')
      }
      checkRows(request.params.ids, grid.current())
      const ids = unlockedRows(request.params.ids, grid.current())
      if (ids.length === 0) throw new BridgeError('Those rows are locked, so they cannot be deleted.')
      await run((resolve, reject) =>
        deleteTableRowAction(ids, grid.projectId, grid.current(), grid.dispatch, resolve, () =>
          reject(new BridgeError('Could not delete the rows. You may not have permission to delete them.'))
        )
      )
      await settle()
      return { deleted: ids.length }
    }
    case 'moveRows': {
      // The grid only lets table owners drag rows to reorder them.
      if (!grid.current().isAdmin) throw new BridgeError('Only table owners can reorder rows.')
      checkRows([...request.params.ids, request.params.before], grid.current())
      for (const id of request.params.ids) {
        await run((resolve, reject) =>
          updateRowPosition(grid.projectId, id, request.params.before, grid.current(), grid.dispatch, resolve, () =>
            reject(new BridgeError('Could not move the rows. You may not have permission to edit them.'))
          )
        )
        await settle()
      }
      return { moved: request.params.ids.length }
    }
    case 'previewRow': {
      // The form view's preview: formulas, joins and scripts worked out for unsaved values; nothing is saved.
      checkColumns(request.params.values, columns)
      const response = await api.previewRow(
        grid.projectId,
        grid.current().viewDetails.publicId,
        request.params.values,
        new AbortController().signal
      )
      return { values: Object.fromEntries(columns.map((column) => [column.id, response.data[column.id] ?? null])) }
    }
  }
}

/** The attachment a page asked for: one file in a visible attachment column of a row the reader's grid holds. */
export function findAttachment(params: FileRequest['params'], data: ISpreadsheetData): IFile {
  const column = viewColumn(params.columnId, data)
  if (column.kind !== 'attachment') throw new BridgeError(`Column "${column.name}" is not an attachment column.`)
  const row = data.rows.find((candidate) => candidate.publicId === params.rowId)
  if (!row) throw new BridgeError(`Row ${params.rowId} is not part of this view.`)
  const value = getRowValue(row, params.columnId, data.columnIdToIndex, data.columnStore)
  const files = (Array.isArray(value) ? value : []) as (IFile | null)[]
  const file = files[params.index]
  if (!file) {
    throw new BridgeError(
      `That cell has ${files.length} ${files.length === 1 ? 'file' : 'files'}, so there is no file at index ${params.index}.`
    )
  }
  return file
}

// Statuses for a file the reader can't open: not signed in, no access, or deleted.
const DENIED = [401, 403, 404]
const deniedMessage = (file: IFile) =>
  `"${file.filename}" can't be read: you don't have access to it, or it has been deleted.`

// Older files are read through their table (column and filename as query parameters), newer ones through a
// signed link, as the attachment preview does.
async function fileResponse(file: IFile, tableId: string, columnId: string, signal: AbortSignal): Promise<Response> {
  if (file.version === 1) {
    const query = new URLSearchParams({ columnId, filename: file.filename })
    return fetch(`${apiPrefix}/table/${tableId}/file?${query}`, {
      headers: { Authorization: getAuthToken() ?? '' },
      signal
    })
  }
  try {
    const signed = await api.signUrl(file.url, signal)
    return await fetch(signed.data.url, { signal })
  } catch (error) {
    if (error instanceof APIError && DENIED.includes(error.rawCode)) throw new BridgeError(deniedMessage(file))
    throw error
  }
}

/**
 * A file's contents, read by the host with the reader's own access and handed to the page as bytes. The page never
 * gets a signed link or a token, and it still can't reach the network itself.
 */
export async function readFile(
  params: FileRequest['params'],
  data: ISpreadsheetData,
  signal: AbortSignal
): Promise<PageFile> {
  const file = findAttachment(params, data)
  const response = await fileResponse(file, data.tableDetails.publicId, params.columnId, signal)
  if (!response.ok) {
    throw new BridgeError(
      DENIED.includes(response.status)
        ? deniedMessage(file)
        : `"${file.filename}" couldn't be downloaded. Check your connection and try again.`
    )
  }
  const bytes = await response.arrayBuffer()
  return {
    name: file.filename,
    extension: file.extension,
    mimetype: file.mimetype,
    size: bytes.byteLength,
    data: bytes
  }
}

/**
 * Saves a file the host has read to the reader's device, from the app's own document: the page's sandbox blocks
 * downloads, so a page can only save an attachment the reader could already download from the grid.
 */
export function saveFile(file: PageFile): void {
  const url = URL.createObjectURL(new Blob([file.data], { type: file.mimetype }))
  const link = document.createElement('a')
  link.href = url
  link.download = file.name
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

export function writeErrorMessage(error: unknown): string {
  if (typeof error === 'string' && error) return error.slice(0, 300)
  if (error instanceof Error && error.message) return error.message.slice(0, 300)
  return 'Unable to save changes.'
}
